// Firebase functions for opportunity analytics and tracking
import { 
  collection, 
  doc, 
  addDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc
} from "firebase/firestore";
import { db } from "./firebase-config";
import { logEvent } from "../analytics";

/**
 * Analytics event types for startup opportunities
 */
export const ANALYTICS_EVENTS = {
  OPPORTUNITY_VIEW: 'opportunity_view',
  OPPORTUNITY_DETAILS_VIEW: 'opportunity_details_view',
  CONNECTION_REQUEST: 'connection_request',
  CONNECTION_APPROVED: 'connection_approved',
  CONNECTION_DECLINED: 'connection_declined',
  CONNECTION_INTRODUCED: 'connection_introduced',
  OPPORTUNITY_SEARCH: 'opportunity_search',
  OPPORTUNITY_FILTER: 'opportunity_filter',
  OPPORTUNITY_SHARE: 'opportunity_share'
};

/**
 * Track opportunity view
 * @param {string} opportunityId - The ID of the opportunity viewed
 * @param {string} userId - The ID of the user viewing (optional for anonymous)
 * @param {Object} context - Additional context about the view
 * @returns {Promise<Object>} - Success status or error
 */
export const trackOpportunityView = async (opportunityId, userId = null, context = {}) => {
  try {
    console.log('Tracking opportunity view:', { opportunityId, userId, context });
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    // Create analytics event
    const analyticsData = {
      eventType: ANALYTICS_EVENTS.OPPORTUNITY_VIEW,
      opportunityId,
      userId,
      timestamp: serverTimestamp(),
      context: {
        source: context.source || 'grid', // 'grid', 'search', 'direct'
        position: context.position || null, // Position in grid/list
        searchTerm: context.searchTerm || null,
        filters: context.filters || null,
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Update opportunity view count
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    await updateDoc(opportunityRef, {
      viewCount: increment(1),
      lastViewedAt: serverTimestamp()
    });
    
    // Track in Firebase Analytics
    await logEvent(ANALYTICS_EVENTS.OPPORTUNITY_VIEW, {
      opportunity_id: opportunityId,
      user_id: userId,
      source: context.source,
      position: context.position
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking opportunity view:', error);
    return {
      success: false,
      error: error.message || 'Failed to track opportunity view'
    };
  }
};

/**
 * Track opportunity details view
 * @param {string} opportunityId - The ID of the opportunity viewed
 * @param {string} userId - The ID of the user viewing (optional for anonymous)
 * @param {Object} context - Additional context about the view
 * @returns {Promise<Object>} - Success status or error
 */
export const trackOpportunityDetailsView = async (opportunityId, userId = null, context = {}) => {
  try {
    console.log('Tracking opportunity details view:', { opportunityId, userId, context });
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    // Create analytics event
    const analyticsData = {
      eventType: ANALYTICS_EVENTS.OPPORTUNITY_DETAILS_VIEW,
      opportunityId,
      userId,
      timestamp: serverTimestamp(),
      context: {
        source: context.source || 'card', // 'card', 'direct_link'
        timeSpent: context.timeSpent || null, // Time spent viewing details
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Update opportunity details view count
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    await updateDoc(opportunityRef, {
      detailsViewCount: increment(1),
      lastDetailsViewedAt: serverTimestamp()
    });
    
    // Track in Firebase Analytics
    await logEvent(ANALYTICS_EVENTS.OPPORTUNITY_DETAILS_VIEW, {
      opportunity_id: opportunityId,
      user_id: userId,
      source: context.source,
      time_spent: context.timeSpent
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking opportunity details view:', error);
    return {
      success: false,
      error: error.message || 'Failed to track opportunity details view'
    };
  }
};

/**
 * Track connection request
 * @param {string} opportunityId - The ID of the opportunity
 * @param {string} userId - The ID of the user making the request
 * @param {string} requestId - The ID of the connection request
 * @param {Object} context - Additional context about the request
 * @returns {Promise<Object>} - Success status or error
 */
export const trackConnectionRequest = async (opportunityId, userId, requestId, context = {}) => {
  try {
    console.log('Tracking connection request:', { opportunityId, userId, requestId, context });
    
    if (!opportunityId || !userId || !requestId) {
      return { success: false, error: 'Opportunity ID, User ID, and Request ID are required' };
    }
    
    // Create analytics event
    const analyticsData = {
      eventType: ANALYTICS_EVENTS.CONNECTION_REQUEST,
      opportunityId,
      userId,
      requestId,
      timestamp: serverTimestamp(),
      context: {
        source: context.source || 'details_modal', // 'details_modal', 'card'
        messageLength: context.messageLength || null,
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Update opportunity connection request count
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    await updateDoc(opportunityRef, {
      connectionRequestCount: increment(1),
      lastConnectionRequestAt: serverTimestamp()
    });
    
    // Track in Firebase Analytics
    await logEvent(ANALYTICS_EVENTS.CONNECTION_REQUEST, {
      opportunity_id: opportunityId,
      user_id: userId,
      request_id: requestId,
      source: context.source,
      message_length: context.messageLength
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking connection request:', error);
    return {
      success: false,
      error: error.message || 'Failed to track connection request'
    };
  }
};

/**
 * Track connection status change
 * @param {string} requestId - The ID of the connection request
 * @param {string} newStatus - The new status ('approved', 'declined', 'introduced')
 * @param {string} adminId - The ID of the admin processing the request
 * @param {Object} context - Additional context
 * @returns {Promise<Object>} - Success status or error
 */
export const trackConnectionStatusChange = async (requestId, newStatus, adminId, context = {}) => {
  try {
    console.log('Tracking connection status change:', { requestId, newStatus, adminId, context });
    
    if (!requestId || !newStatus || !adminId) {
      return { success: false, error: 'Request ID, new status, and admin ID are required' };
    }
    
    // Get the connection request to get opportunity and user info
    const requestRef = doc(db, 'connection_requests', requestId);
    const requestSnap = await getDoc(requestRef);
    
    if (!requestSnap.exists()) {
      return { success: false, error: 'Connection request not found' };
    }
    
    const requestData = requestSnap.data();
    
    // Determine event type based on status
    let eventType;
    switch (newStatus) {
      case 'approved':
        eventType = ANALYTICS_EVENTS.CONNECTION_APPROVED;
        break;
      case 'declined':
        eventType = ANALYTICS_EVENTS.CONNECTION_DECLINED;
        break;
      case 'introduced':
        eventType = ANALYTICS_EVENTS.CONNECTION_INTRODUCED;
        break;
      default:
        return { success: false, error: 'Invalid status for tracking' };
    }
    
    // Create analytics event
    const analyticsData = {
      eventType,
      opportunityId: requestData.opportunityId,
      userId: requestData.userId,
      requestId,
      adminId,
      timestamp: serverTimestamp(),
      context: {
        previousStatus: context.previousStatus || 'pending',
        processingTime: context.processingTime || null, // Time from request to processing
        adminNotes: context.adminNotes || null,
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Update opportunity metrics based on status
    const opportunityRef = doc(db, 'startup_opportunities', requestData.opportunityId);
    const updateData = {};
    
    if (newStatus === 'approved') {
      updateData.connectionApprovedCount = increment(1);
    } else if (newStatus === 'declined') {
      updateData.connectionDeclinedCount = increment(1);
    } else if (newStatus === 'introduced') {
      updateData.connectionIntroducedCount = increment(1);
      updateData.lastSuccessfulConnectionAt = serverTimestamp();
    }
    
    if (Object.keys(updateData).length > 0) {
      await updateDoc(opportunityRef, updateData);
    }
    
    // Track in Firebase Analytics
    await logEvent(eventType, {
      opportunity_id: requestData.opportunityId,
      user_id: requestData.userId,
      request_id: requestId,
      admin_id: adminId,
      processing_time: context.processingTime
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking connection status change:', error);
    return {
      success: false,
      error: error.message || 'Failed to track connection status change'
    };
  }
};

/**
 * Track search activity
 * @param {string} searchTerm - The search term used
 * @param {Object} filters - Applied filters
 * @param {number} resultCount - Number of results returned
 * @param {string} userId - The ID of the user searching (optional for anonymous)
 * @param {Object} context - Additional context
 * @returns {Promise<Object>} - Success status or error
 */
export const trackOpportunitySearch = async (searchTerm, filters = {}, resultCount = 0, userId = null, context = {}) => {
  try {
    console.log('Tracking opportunity search:', { searchTerm, filters, resultCount, userId, context });
    
    // Create analytics event
    const analyticsData = {
      eventType: ANALYTICS_EVENTS.OPPORTUNITY_SEARCH,
      userId,
      timestamp: serverTimestamp(),
      searchData: {
        searchTerm: searchTerm || null,
        filters,
        resultCount,
        searchDuration: context.searchDuration || null,
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Track in Firebase Analytics
    await logEvent(ANALYTICS_EVENTS.OPPORTUNITY_SEARCH, {
      search_term: searchTerm,
      result_count: resultCount,
      user_id: userId,
      has_filters: Object.keys(filters).length > 0,
      filter_count: Object.keys(filters).length
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking opportunity search:', error);
    return {
      success: false,
      error: error.message || 'Failed to track opportunity search'
    };
  }
};

/**
 * Track filter usage
 * @param {Object} appliedFilters - The filters that were applied
 * @param {number} resultCount - Number of results after filtering
 * @param {string} userId - The ID of the user filtering (optional for anonymous)
 * @param {Object} context - Additional context
 * @returns {Promise<Object>} - Success status or error
 */
export const trackOpportunityFilter = async (appliedFilters = {}, resultCount = 0, userId = null, context = {}) => {
  try {
    console.log('Tracking opportunity filter:', { appliedFilters, resultCount, userId, context });
    
    // Create analytics event
    const analyticsData = {
      eventType: ANALYTICS_EVENTS.OPPORTUNITY_FILTER,
      userId,
      timestamp: serverTimestamp(),
      filterData: {
        appliedFilters,
        resultCount,
        filterCount: Object.keys(appliedFilters).length,
        ...context
      }
    };
    
    // Store in analytics collection
    await addDoc(collection(db, 'opportunity_analytics'), analyticsData);
    
    // Track in Firebase Analytics
    await logEvent(ANALYTICS_EVENTS.OPPORTUNITY_FILTER, {
      filter_types: Object.keys(appliedFilters),
      filter_count: Object.keys(appliedFilters).length,
      result_count: resultCount,
      user_id: userId
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error tracking opportunity filter:', error);
    return {
      success: false,
      error: error.message || 'Failed to track opportunity filter'
    };
  }
};

/**
 * Get opportunity analytics summary
 * @param {string} opportunityId - The ID of the opportunity
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and analytics data or error
 */
export const getOpportunityAnalytics = async (opportunityId, options = {}) => {
  try {
    console.log('Fetching opportunity analytics:', { opportunityId, options });
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    const {
      startDate = null,
      endDate = null,
      eventTypes = null,
      limit: limitCount = 100
    } = options;
    
    // Build query for analytics events
    let analyticsQuery = collection(db, 'opportunity_analytics');
    const queryConstraints = [where('opportunityId', '==', opportunityId)];
    
    // Add date filters if provided
    if (startDate) {
      queryConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      queryConstraints.push(where('timestamp', '<=', endDate));
    }
    
    // Add event type filter if provided
    if (eventTypes && eventTypes.length > 0) {
      queryConstraints.push(where('eventType', 'in', eventTypes));
    }
    
    // Add ordering and limit
    queryConstraints.push(orderBy('timestamp', 'desc'));
    queryConstraints.push(limit(limitCount));
    
    analyticsQuery = query(analyticsQuery, ...queryConstraints);
    
    // Execute query
    const snapshot = await getDocs(analyticsQuery);
    
    // Transform data
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get opportunity metrics from the opportunity document
    const opportunityRef = doc(db, 'startup_opportunities', opportunityId);
    const opportunitySnap = await getDoc(opportunityRef);
    
    const opportunityMetrics = opportunitySnap.exists() ? {
      viewCount: opportunitySnap.data().viewCount || 0,
      detailsViewCount: opportunitySnap.data().detailsViewCount || 0,
      connectionRequestCount: opportunitySnap.data().connectionRequestCount || 0,
      connectionApprovedCount: opportunitySnap.data().connectionApprovedCount || 0,
      connectionDeclinedCount: opportunitySnap.data().connectionDeclinedCount || 0,
      connectionIntroducedCount: opportunitySnap.data().connectionIntroducedCount || 0,
      lastViewedAt: opportunitySnap.data().lastViewedAt || null,
      lastConnectionRequestAt: opportunitySnap.data().lastConnectionRequestAt || null,
      lastSuccessfulConnectionAt: opportunitySnap.data().lastSuccessfulConnectionAt || null
    } : {};
    
    return {
      success: true,
      data: {
        events,
        metrics: opportunityMetrics,
        summary: {
          totalEvents: events.length,
          eventTypes: [...new Set(events.map(e => e.eventType))],
          uniqueUsers: [...new Set(events.filter(e => e.userId).map(e => e.userId))].length
        }
      }
    };
  } catch (error) {
    console.error('Error fetching opportunity analytics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch opportunity analytics'
    };
  }
};

/**
 * Get overall analytics summary for all opportunities
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and analytics data or error
 */
export const getOverallAnalytics = async (options = {}) => {
  try {
    console.log('Fetching overall analytics:', options);
    
    const {
      startDate = null,
      endDate = null,
      limit: limitCount = 1000
    } = options;
    
    // Build query for analytics events
    let analyticsQuery = collection(db, 'opportunity_analytics');
    const queryConstraints = [];
    
    // Add date filters if provided
    if (startDate) {
      queryConstraints.push(where('timestamp', '>=', startDate));
    }
    if (endDate) {
      queryConstraints.push(where('timestamp', '<=', endDate));
    }
    
    // Add ordering and limit
    queryConstraints.push(orderBy('timestamp', 'desc'));
    queryConstraints.push(limit(limitCount));
    
    if (queryConstraints.length > 0) {
      analyticsQuery = query(analyticsQuery, ...queryConstraints);
    }
    
    // Execute query
    const snapshot = await getDocs(analyticsQuery);
    
    // Transform and analyze data
    const events = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calculate summary metrics
    const summary = {
      totalEvents: events.length,
      uniqueOpportunities: [...new Set(events.filter(e => e.opportunityId).map(e => e.opportunityId))].length,
      uniqueUsers: [...new Set(events.filter(e => e.userId).map(e => e.userId))].length,
      eventBreakdown: {},
      topOpportunities: {},
      conversionMetrics: {
        viewToDetailsRate: 0,
        detailsToConnectionRate: 0,
        connectionToIntroductionRate: 0
      }
    };
    
    // Calculate event breakdown
    events.forEach(event => {
      summary.eventBreakdown[event.eventType] = (summary.eventBreakdown[event.eventType] || 0) + 1;
      
      if (event.opportunityId) {
        summary.topOpportunities[event.opportunityId] = (summary.topOpportunities[event.opportunityId] || 0) + 1;
      }
    });
    
    // Calculate conversion rates
    const viewEvents = summary.eventBreakdown[ANALYTICS_EVENTS.OPPORTUNITY_VIEW] || 0;
    const detailsEvents = summary.eventBreakdown[ANALYTICS_EVENTS.OPPORTUNITY_DETAILS_VIEW] || 0;
    const connectionEvents = summary.eventBreakdown[ANALYTICS_EVENTS.CONNECTION_REQUEST] || 0;
    const introductionEvents = summary.eventBreakdown[ANALYTICS_EVENTS.CONNECTION_INTRODUCED] || 0;
    
    if (viewEvents > 0) {
      summary.conversionMetrics.viewToDetailsRate = (detailsEvents / viewEvents * 100).toFixed(2);
    }
    if (detailsEvents > 0) {
      summary.conversionMetrics.detailsToConnectionRate = (connectionEvents / detailsEvents * 100).toFixed(2);
    }
    if (connectionEvents > 0) {
      summary.conversionMetrics.connectionToIntroductionRate = (introductionEvents / connectionEvents * 100).toFixed(2);
    }
    
    return {
      success: true,
      data: {
        events,
        summary
      }
    };
  } catch (error) {
    console.error('Error fetching overall analytics:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch overall analytics'
    };
  }
};